// src/components/profile/SubscriptionStatusCard.tsx

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { SubscriptionDisplay } from '../../hooks/useSubscription';

interface SubscriptionStatusCardProps {
  display?: SubscriptionDisplay;
  loading?: boolean;
  clickable?: boolean;
  onPreloadSubscription?: () => void;
}

const SubscriptionStatusCard: React.FC<SubscriptionStatusCardProps> = ({
  display,
  loading = false,
  clickable = false,
  onPreloadSubscription
}) => {
  const navigate = useNavigate();

  // 處理卡片點擊 - 根據用戶狀態導向不同頁面
  const handleCardClick = () => {
    if (!clickable) return;

    const displayData = display || {
      planDisplay: 'Free',
      rightText: 'Upgrade',
      showUpgrade: true,
      badge: undefined
    };

    // 免費用戶導向方案選擇頁面，付費用戶導向管理頁面
    if (displayData.showUpgrade) {
      navigate('/plans');
    } else {
      navigate('/profile/subscription');
    }
  };
  // 載入中狀態 - 更詳細的骨架屏
  if (loading) {
    return (
      <div
        className={`w-full bg-white border-2 border-gray-200 rounded-xl p-4 mb-4 ${clickable ? 'cursor-pointer hover:border-gray-300 transition-colors' : ''}`}
        onClick={clickable ? handleCardClick : undefined}
        onMouseEnter={clickable ? onPreloadSubscription : undefined}
      >
        <div className="animate-pulse">
          <div className="flex items-center justify-between">
            {/* 左側：計畫名稱 + 可能的標籤區域 */}
            <div className="flex items-center space-x-2">
              <div className="h-5 bg-gray-200 rounded w-12"></div>
              <div className="h-4 bg-gray-100 rounded w-16 opacity-60"></div>
            </div>

            {/* 右側：狀態資訊 */}
            <div className="h-4 bg-gray-200 rounded w-20"></div>
          </div>

          {/* 模擬可能的額外資訊行 */}
          <div className="mt-3 space-y-2 opacity-40">
            <div className="h-3 bg-gray-100 rounded w-3/4"></div>
            <div className="h-3 bg-gray-100 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  // 如果沒有顯示資料，顯示預設狀態
  const displayData = display || {
    planDisplay: 'Free',
    rightText: 'Upgrade',
    showUpgrade: true,
    badge: undefined
  };

  return (
    <div
      className={`w-full bg-white border-2 border-gray-200 rounded-xl p-4 mb-4 ${clickable ? 'cursor-pointer hover:border-gray-300 hover:shadow-sm transition-all duration-200' : ''}`}
      onClick={clickable ? handleCardClick : undefined}
      onMouseEnter={clickable ? onPreloadSubscription : undefined}
    >
      <div className="flex items-center justify-between">
        {/* 左側：計畫名稱 + 標籤 */}
        <div className="flex items-center space-x-2">
          <span className="font-semibold text-gray-900">
            {displayData.planDisplay}
          </span>
          {displayData.badge && (
            <span className={displayData.badge.className}>
              {displayData.badge.text}
            </span>
          )}
        </div>
        
        {/* 右側：狀態資訊 */}
        <div className="flex items-center text-sm text-gray-600">
          {displayData.showUpgrade ? (
            <span className="text-sky-600 font-medium mr-1">
              {displayData.rightText}
            </span>
          ) : (
            <span className="mr-1">{displayData.rightText}</span>
          )}
          {clickable && <ChevronRight size={16} className="text-gray-400" />}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionStatusCard;